import { getPrimaryMenuItems, MenuItem, PrimaryMenuItem } from "@/lib/menu"
import { useState } from "react"
import Link from "next/link"
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { AnimatePresence, motion } from "framer-motion"

// A single item in the mobile menu. Handles its own state for the accordion.
const MobileMenuItem = ({
  item,
  handleLinkClick,
}: {
  item: PrimaryMenuItem
  handleLinkClick: () => void
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const hasSubPages = item.subPages && item.subPages.length > 0

  // If there are no sub-pages, render a simple link.
  if (!hasSubPages) {
    return (
      <Link
        href={item.href}
        className="block px-6 py-4 text-base font-medium text-default transition-colors hover:text-primary"
        onClick={handleLinkClick}
      >
        {item.label}
      </Link>
    )
  }

  // If there are sub-pages, render an accordion button.
  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between px-6 py-4 text-base font-medium text-default transition-colors hover:text-primary"
      >
        <span>{item.label}</span>
        <ChevronDownIcon
          className={`h-5 w-5 text-tertiary transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.ul
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-l border-border pl-10"
          >
            {item.subPages!.map((subItem: MenuItem) => (
              <li key={subItem.key}>
                <Link
                  href={subItem.href}
                  className="block py-3 pr-6 text-sm text-tertiary transition-colors hover:text-primary"
                  onClick={handleLinkClick}
                >
                  {subItem.label}
                </Link>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </>
  )
}

// Mobile menu panel that slides in from the right
export const MobileMenu = ({
  setOpen,
}: {
  setOpen: (open: boolean) => void
}) => {
  const menuItems = getPrimaryMenuItems()

  const handleLinkClick = () => {
    setOpen(false)
  }

  return (
    // We use a React fragment to render the backdrop and panel as siblings.
    // This prevents the panel from inheriting the backdrop's opacity.
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 bg-black/25 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.2, ease: "easeInOut" }}
        className="fixed inset-y-0 right-0 z-[51] w-full max-w-sm shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="border-l border-border bg-[var(--color-surface-elevated)]">
          <div className="flex items-center justify-between p-6">
            <h3 className="text-lg font-semibold">Menu</h3>
            <button
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-tertiary hover:text-default"
              aria-label="Close menu"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div
            className="overflow-y-auto"
            style={{ height: "calc(100vh - 4.5rem)" }}
          >
            <ul className="divide-y divide-border">
              {menuItems.map(item => (
                <li key={item.key}>
                  <MobileMenuItem
                    item={item}
                    handleLinkClick={handleLinkClick}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>
    </>
  )
}
